import { RefObject, useCallback, useState } from 'react';

import { MentionTriggerState } from './MentionTextarea.types';
import { detectTrigger } from './MentionTextarea.utils';

const INACTIVE: MentionTriggerState = {
    isActive: false,
    query: '',
    triggerIndex: -1,
    caretRect: null,
};

/**
 * Serialize the text *before the caret* using the same rules as
 * `LinkifyTextarea.getTextContent` (text nodes verbatim, `<br>` → `\n`, and
 * leading/trailing newlines around DIV/P blocks). The length of this string is
 * the caret's character offset within the input's `value`, which is what the
 * pure {@link detectTrigger} operates on.
 *
 * Exported for unit testing: the DOM walk runs fine in jsdom (only layout
 * rects, handled separately, are browser-only).
 */
export function getPreCaretText(
    container: HTMLElement,
    anchorNode: Node,
    anchorOffset: number,
): string {
    let text = '';
    let done = false;

    const processNode = (node: Node, parent: Node, index: number) => {
        if (done) return;

        if (node.nodeType === Node.TEXT_NODE) {
            if (node === anchorNode) {
                text += (node.textContent ?? '').slice(0, anchorOffset);
                done = true;
                return;
            }
            text += node.textContent ?? '';
            return;
        }

        if (node.nodeName === 'BR') {
            text += '\n';
            return;
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
            const tag = node.nodeName;
            if ((tag === 'DIV' || tag === 'P') && text.length > 0 && !text.endsWith('\n')) {
                text += '\n';
            }
            if (node === anchorNode) {
                // Caret sits between child nodes: include the first `anchorOffset`.
                for (let j = 0; j < anchorOffset && !done; j++) {
                    processNode(node.childNodes[j], node, j);
                }
                done = true;
                return;
            }
            processChildren(node);
            if (
                (tag === 'DIV' || tag === 'P') &&
                index < parent.childNodes.length - 1 &&
                !text.endsWith('\n')
            ) {
                text += '\n';
            }
        }
    };

    function processChildren(parent: Node) {
        for (let i = 0; i < parent.childNodes.length && !done; i++) {
            processNode(parent.childNodes[i], parent, i);
        }
    }

    if (anchorNode === container) {
        for (let j = 0; j < anchorOffset && !done; j++) {
            processNode(container.childNodes[j], container, j);
        }
        return text;
    }

    processChildren(container);
    return text;
}

/**
 * Read the current caret offset within `editable`, or null when there is no
 * selection inside it.
 */
export function getCaretOffset(editable: HTMLElement): number | null {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    const { anchorNode, anchorOffset } = selection;
    if (!anchorNode || !editable.contains(anchorNode)) return null;

    return getPreCaretText(editable, anchorNode, anchorOffset).length;
}

/**
 * Walk the editable's nodes to find the DOM position (node + offset) for a
 * given character offset, applying the same `<br>`/DIV/P newline accounting as
 * {@link getPreCaretText} so the mapping round-trips.
 */
function charOffsetToPosition(
    container: HTMLElement,
    offset: number,
): { node: Node; offset: number } | null {
    let remaining = offset;
    let result: { node: Node; offset: number } | null = null;

    const walk = (node: Node) => {
        if (result) return;
        if (node.nodeType === Node.TEXT_NODE) {
            const len = (node.textContent ?? '').length;
            if (remaining <= len) {
                result = { node, offset: remaining };
                return;
            }
            remaining -= len;
        } else if (node.nodeName === 'BR') {
            if (remaining === 0) {
                result = { node: node.parentNode as Node, offset: indexOf(node) + 1 };
                return;
            }
            remaining -= 1;
        } else {
            for (let i = 0; i < node.childNodes.length && !result; i++) {
                walk(node.childNodes[i]);
            }
        }
    };

    walk(container);
    return result;
}

function indexOf(node: Node): number {
    let i = 0;
    let sibling = node.previousSibling;
    while (sibling) {
        i++;
        sibling = sibling.previousSibling;
    }
    return i;
}

/**
 * Place the caret at a character offset within the editable. Used to restore
 * the cursor after a programmatic mention insertion rewrites the content.
 *
 * Behind a v8-ignore: Range/Selection placement is browser behavior that JSDOM
 * only partially models, so it is exercised manually rather than in unit tests.
 */
/* v8 ignore start -- Range/Selection placement is browser-only behavior */
export function setCaretOffset(editable: HTMLElement, offset: number): void {
    const position = charOffsetToPosition(editable, offset);
    if (!position) return;
    const selection = window.getSelection();
    if (!selection) return;
    const range = document.createRange();
    range.setStart(position.node, position.offset);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
}

function getCaretRect(editable: HTMLElement): DOMRect {
    const selection = window.getSelection();
    // Range.getBoundingClientRect is layout-dependent and absent in some
    // non-browser environments (e.g. JSDOM), so guard before calling it.
    if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0).cloneRange();
        range.collapse(true);
        if (typeof range.getBoundingClientRect === 'function') {
            const rect = range.getBoundingClientRect();
            if (rect && (rect.width || rect.height || rect.top || rect.left)) {
                return rect;
            }
        }
    }
    // A collapsed caret at a node boundary (or an environment without Range
    // geometry) yields no usable rect; fall back to the editable's own box so
    // the dropdown still anchors sensibly.
    return editable.getBoundingClientRect();
}
/* v8 ignore stop */

/**
 * Detects an in-progress `@` mention from the live caret position and tracks
 * the query the user is typing. The owning component calls `detect(value)` on
 * every input/keyup/selection change and `close()` to dismiss (Escape, space,
 * after a selection is made).
 */
export function useMentionTrigger(containerRef: RefObject<HTMLElement | null>) {
    const [triggerState, setTriggerState] = useState<MentionTriggerState>(INACTIVE);

    const getEditable = useCallback(
        (): HTMLElement | null =>
            containerRef.current?.querySelector<HTMLElement>('[contenteditable="true"]') ?? null,
        [containerRef],
    );

    const detect = useCallback(
        (value: string) => {
            const editable = getEditable();
            if (!editable) return;

            const caret = getCaretOffset(editable);
            if (caret === null) {
                setTriggerState(INACTIVE);
                return;
            }

            const detection = detectTrigger(value, caret);
            if (!detection.isActive) {
                setTriggerState(INACTIVE);
                return;
            }

            setTriggerState({ ...detection, caretRect: getCaretRect(editable) });
        },
        [getEditable],
    );

    const close = useCallback(() => setTriggerState(INACTIVE), []);

    return { triggerState, detect, close, getEditable };
}
